<template>
  <TransitionGroup
    tag="ol"
    name="rank"
    class="standings"
    aria-label="Standings"
    :style="{ '--cols': rows.length <= 3 ? 1 : 2 }"
  >
    <li
      v-for="row in rows"
      :key="row.id"
      class="standing"
      :class="{ 'is-leader': row.isLeader, 'is-choosing': row.isChoosing }"
      :title="row.title"
    >
      <span class="rank">
        <span v-if="hasRounds" :aria-label="`Rank ${row.rank}`">{{ row.rank }}</span>
      </span>
      <span class="who">
        <span v-if="row.isChoosing" class="choosing-dot" aria-hidden="true"></span>
        <span class="name">{{ row.name }}</span>
        <span v-if="row.isChoosing" class="visually-hidden">, choosing the next game</span>
        <span
          v-if="row.change"
          class="move"
          :class="row.change > 0 ? 'is-up' : 'is-down'"
          :aria-label="`${row.change > 0 ? 'up' : 'down'} ${Math.abs(row.change)}`"
        >
          {{ row.change > 0 ? '▲' : '▼' }}{{ Math.abs(row.change) }}
        </span>
      </span>
      <span class="total">
        {{ formatTotal(row.total) }}
      </span>
    </li>
  </TransitionGroup>
</template>

<script setup>
import { computed } from 'vue';
import { formatPoints, formatTotal, playerTotal } from '../utils/scores';

const props = defineProps({
  players: {
    type: Array,
    required: true
  },
  rounds: {
    type: Array,
    required: true
  },
  currentPlayerIndex: {
    type: Number,
    default: -1
  },
  showMoves: {
    type: Boolean,
    default: true
  }
});

const hasRounds = computed(() => props.rounds.length > 0);

const rankWithTies = (values) =>
  values.map((value) => values.filter((other) => other > value).length + 1);

const rows = computed(() => {
  const lastRound = props.rounds[props.rounds.length - 1];
  const totals = props.players.map((player) => playerTotal(props.rounds, player.id));
  const lasts = props.players.map(
    (player) => lastRound?.scores.find((s) => s.playerId === player.id)?.score ?? 0
  );
  const ranks = rankWithTies(totals);
  const previousRanks = rankWithTies(totals.map((total, i) => total - lasts[i]));
  const showMoves = props.showMoves && props.rounds.length >= 2;

  const list = props.players.map((player, i) => ({
    id: player.id,
    name: player.name,
    seat: i,
    total: totals[i],
    rank: ranks[i],
    change: showMoves ? previousRanks[i] - ranks[i] : 0,
    isLeader: hasRounds.value && ranks[i] === 1,
    isChoosing: i === props.currentPlayerIndex,
    title: hasRounds.value ? `${player.name}: ${formatPoints(lasts[i])} last round` : player.name
  }));

  if (!hasRounds.value) return list;
  return list.sort((a, b) => a.rank - b.rank || a.seat - b.seat);
});
</script>

<style scoped>
.standings {
  list-style: none;
  columns: var(--cols);
  column-gap: 1.75rem;
}

.standing {
  break-inside: avoid;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2.25rem;
}

.rank {
  flex: none;
  width: 1.125rem;
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.0625rem;
  line-height: 1;
  color: var(--on-table-muted);
}

.who {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.choosing-dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: var(--focus);
  box-shadow: 0 0 0 3px rgba(110, 146, 255, 0.22);
}

.name {
  min-width: 0;
  font-weight: 500;
  font-size: 0.9375rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.is-choosing .name {
  font-weight: 600;
}

.move {
  flex: none;
  font-size: 0.6875rem;
  font-weight: 700;
}

.move.is-up {
  color: var(--on-table-muted);
}

.move.is-down {
  color: var(--red-on-table);
}

.total {
  flex: none;
  margin-left: auto;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1;
}

.is-leader .rank,
.is-leader .total {
  color: var(--gold);
}

.is-leader .name {
  font-weight: 600;
}

.rank-move {
  transition: transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
}

@media (min-width: 560px) {
  .standings {
    column-gap: 2rem;
  }

  .standing {
    min-height: 2.5rem;
  }

  .total {
    font-size: 1.375rem;
  }
}
</style>
